import { createReducer, on } from '@ngrx/store';

import {
  EMPTY_ADVANCED_FEASIBILITY_FILTERS,
  INITIAL_LOCATION_UPLOAD_STATE,
  SurveyKind,
} from '@ps/survey-utils';

import {
  DEFAULT_SURVEY_CHILD_KEY,
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
} from '../survey-creation-state.constants';
import { SurveyCreationAudienceState } from '../survey-creation-state.model';
import { coreContextOf } from '../../utils/core-context/survey-creation-action-core-context.helper';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
  SurveyCreationChildrenActions,
  SurveyCreationMcApiActions,
  SurveyCreationPreDraftActions,
} from './survey-creation-audience.actions';
import { SurveyCreationLaunchActions } from '../launch/survey-creation-launch.actions';
import { hasLocaleChanged } from './utils/locale-changes.helper';
import {
  buildPreDraftChildren,
  filterMetadataForChild,
  initializeAudienceState,
  isLoadedAudienceSibling,
  omitChild,
  patchChildren,
  resolveActiveChildKey,
} from './utils/audience-children.helper';
import {
  CHILD_SCOPE,
  delegateRecordToDefaultChild,
} from '../survey-creation-child-registry.helper';
import { surveyTagScope } from './utils/survey-tags-changes.helper';
import {
  buildLoadedChild,
  hydrateChildFromSurvey,
} from './domain/build-audience-form-state-from-survey';

export const surveyCreationAudienceReducer = createReducer(
  INITIAL_SURVEY_CREATION_AUDIENCE_STATE,

  // Cross-listens to both stage-specific init actions — the save pipeline
  // reads audience child state regardless of which page opened it. See
  // SurveyCreationAudienceActions.init's doc comment.
  on(
    SurveyCreationAudienceActions.init,
    SurveyCreationLaunchActions.init,
    (state, action): SurveyCreationAudienceState => {
      const { surveyId } = action;
      const { rootSurveyId } = coreContextOf(action);

      const alreadyTargeted = !!surveyId && rootSurveyId === surveyId;

      if (alreadyTargeted) {
        return state;
      }

      // A locale switch re-`init`s with a sibling's id. Mirrors the same
      // guard in core's init handler - resetting would wipe every
      // sibling's already-loaded form state back to defaults.
      if (!!surveyId && isLoadedAudienceSibling(state.children, surveyId)) {
        return state;
      }

      return initializeAudienceState(surveyId);
    },
  ),

  on(
    SurveyCreationCoreActions.resetActiveSurveyId,
    (state, action): SurveyCreationAudienceState => ({
      ...state,
      children: delegateRecordToDefaultChild(
        state.children,
        coreContextOf(action).activeChildKey ?? DEFAULT_SURVEY_CHILD_KEY,
      ),
    }),
  ),

  on(
    SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess,
    (state, action): SurveyCreationAudienceState => ({
      ...state,
      children: {
        ...state.children,
        ...Object.fromEntries(
          action.surveys
            .filter(({ id }) => !state.children[id])
            .map((survey) => [survey.id, buildLoadedChild(survey)]),
        ),
      },
    }),
  ),

  // The added/removed locale's registry row (survey, error, participant) is
  // the `core` slice's business; audience only owns the form state.
  on(
    SurveyCreationMcApiActions.addSurveyChildSuccess,
    (state, { survey }): SurveyCreationAudienceState => ({
      ...state,
      children: {
        ...state.children,
        [survey.id]: buildLoadedChild(survey),
      },
    }),
  ),

  on(
    SurveyCreationMcApiActions.deleteSurveyChildSuccess,
    (state, { surveyId }): SurveyCreationAudienceState => ({
      ...state,
      children: omitChild(state.children, surveyId),
    }),
  ),

  // Pre-draft only: before a surveyId exists, each locale gets its own
  // child. Once rootSurveyId is set, the survey is real and its locale is fixed.
  on(
    SurveyCreationPreDraftActions.buildPreDraftChildren,
    (state, action): SurveyCreationAudienceState =>
      coreContextOf(action).rootSurveyId
        ? state
        : {
            ...state,
            children: buildPreDraftChildren(state.children, action.localeData),
          },
  ),

  // Hydrate the in-memory survey from the API response, and mark the linked-tags
  // follow-up request (loadSurveyTagsForEdit$) as in flight.
  on(
    SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess,
    (state, action): SurveyCreationAudienceState => {
      const { survey } = action;
      return patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.keys([survey.id]),
        (rootSurvey) => ({
          ...hydrateChildFromSurvey(survey),
          tagLinks: { ...rootSurvey.tagLinks, loadingLinkedTags: true },
        }),
      );
    },
  ),

  // Seeds the active child's own form state (a project's active child differs
  // from the root just hydrated above).
  on(
    SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        hydrateChildFromSurvey(action.survey),
      ),
  ),

  on(
    SurveyCreationAudienceActions.updateMargin,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { margin: action.margin },
      ),
  ),

  on(
    SurveyCreationAudienceActions.queueHighCpiConfirmation,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          pendingHighCpiConfirmations: [
            ...child.pendingHighCpiConfirmations,
            action.cpi,
          ],
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.clearHighCpiConfirmationQueue,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        { pendingHighCpiConfirmations: [] },
      ),
  ),

  on(
    SurveyCreationAudienceActions.updateMetadata,
    (state, action): SurveyCreationAudienceState => {
      const { metadata } = action;
      const isMultiLocale = Object.keys(state.children).length > 1;

      return patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        action.scope,
        (child, key) => {
          const childMetadata = isMultiLocale
            ? filterMetadataForChild(metadata, key)
            : metadata;
          return {
            metadata: childMetadata,
            advancedFeasibilityFilters: hasLocaleChanged(
              child.metadata,
              childMetadata,
            )
              ? { ...EMPTY_ADVANCED_FEASIBILITY_FILTERS }
              : child.advancedFeasibilityFilters,
          };
        },
      );
    },
  ),

  on(
    SurveyCreationChildrenActions.patchChildMetadata,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        action.scope,
        (child) => ({
          metadata: {
            ...child.metadata,
            value: child.metadata.value && {
              ...child.metadata.value,
              ...action.changes,
            },
            isDirty: true,
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.updateQualifications,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action).activeChildKey, action.scope, {
        qualifications: action.qualifications,
      }),
  ),

  on(
    SurveyCreationAudienceActions.updateQuotas,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(state, coreContextOf(action).activeChildKey, action.scope, {
        quotas: action.quotas,
      }),
  ),

  on(
    SurveyCreationAudienceActions.uploadLocationCodes,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        {
          locationFileUpload: {
            qualificationCode: action.qualificationCode,
            loading: true,
            error: null,
            filePath: null,
            result: null,
          },
        },
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.uploadLocationCodesSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        {
          locationFileUpload: {
            qualificationCode: action.qualificationCode,
            loading: false,
            error: null,
            filePath: action.result.filePath,
            result: action.result,
          },
        },
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.uploadLocationCodesFailure,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        {
          locationFileUpload: {
            qualificationCode: action.qualificationCode,
            loading: false,
            error: action.error,
            filePath: null,
            result: null,
          },
        },
      ),
  ),

  on(
    SurveyCreationAudienceActions.clearLocationCodes,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) =>
          child.locationFileUpload.qualificationCode ===
          action.qualificationCode
            ? { locationFileUpload: { ...INITIAL_LOCATION_UPLOAD_STATE } }
            : {},
      ),
  ),

  on(
    SurveyCreationAudienceActions.applyAdvancedFilters,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        {
          advancedFeasibilityFilters: action.filters,
        },
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.createStandardSurveySuccess,
    SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
    (state, action): SurveyCreationAudienceState => {
      const { survey } = action;
      const { activeChildKey } = coreContextOf(action);
      const oldChild =
        state.children[resolveActiveChildKey(state, activeChildKey)];

      return {
        children: {
          [survey.id]: { ...oldChild },
        },
      };
    },
  ),

  on(
    SurveyCreationCoreApiActions.saveSurveySuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        SurveyKind.isGroupMc(action.survey)
          ? CHILD_SCOPE.ALL
          : CHILD_SCOPE.ACTIVE,
        (child) => ({
          metadata: { ...child.metadata, isDirty: false },
          margin: { ...child.margin, isDirty: false },
          qualifications: { ...child.qualifications, isDirty: false },
          quotas: { ...child.quotas, isDirty: false },
        }),
      ),
  ),

  // No cloneSurveySuccess handler: navigateToEditSurvey$ dispatches reset()
  // right after it, so anything written here would be wiped before it is read.

  on(
    SurveyCreationAudienceApiActions.loadSurveyTagsSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        surveyTagScope(coreContextOf(action).kind),
        (child) => ({
          tagLinks: {
            ...child.tagLinks,
            linkedTagIds: action.tagIds,
            loadingLinkedTags: false,
            selectedTagIds: action.tagIds,
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.loadSurveyTagsFailure,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          tagLinks: {
            ...child.tagLinks,
            linkedTagIds: [],
            loadingLinkedTags: false,
            selectedTagIds: [],
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.selectTags,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          tagLinks: { ...child.tagLinks, selectedTagIds: action.tagIds },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.linkSurveyTagsSuccess,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          tagLinks: {
            ...child.tagLinks,
            linkedTagIds: action.tagIds,
            linkTagsError: null,
          },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceApiActions.linkSurveyTagsFailure,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          tagLinks: { ...child.tagLinks, linkTagsError: action.error },
        }),
      ),
  ),

  on(
    SurveyCreationAudienceActions.setSuppliersMetaDataBlendId,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          suppliersMetaData: {
            ...child.suppliersMetaData,
            blendId: action.blendId,
          },
        }),
      ),
  ),

  // Partial merge — the caller sends only the facts it changed (priceOverride
  // from the auto PurePrice recompute, defaultPurePriceOverride when the user
  // takes manual pricing control); every other field is left untouched.
  on(
    SurveyCreationAudienceActions.setSuppliersMetaData,
    (state, action): SurveyCreationAudienceState =>
      patchChildren(
        state,
        coreContextOf(action).activeChildKey,
        CHILD_SCOPE.ACTIVE,
        (child) => ({
          suppliersMetaData: {
            ...child.suppliersMetaData,
            ...action.metaData,
          },
        }),
      ),
  ),

  on(
    SurveyCreationCoreActions.reset,
    (): SurveyCreationAudienceState => INITIAL_SURVEY_CREATION_AUDIENCE_STATE,
  ),
);
